import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  inject,
  Injector,
  input,
  OnInit,
  Output,
  runInInjectionContext,
  signal
} from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { OptionItem } from '../../molecules/option-item/option-item';
import { SidebarItemComponent } from '../../molecules/sidebar-item/sidebar-item.component';
import { PanelMenuModule } from 'primeng/panelmenu';
import { TooltipModule } from 'primeng/tooltip';

/**
 * Componente que representa una lista de opciones.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    TieredMenuModule,
    BadgeModule,
    RippleModule,
    PanelMenuModule,
    TooltipModule
  ],
  template: `
    <div class="vertical-menu-container">
      <p-panelmenu [model]="menuItems()" class="w-full md:w-10">
        <ng-template #item let-item>
          <a
            pRipple
            class="flex items-center px-4 py-2 cursor-pointer group"
            [class.menu-item-active]="selectedItemId() === item.data?.id"
            [pTooltip]="!isExpanded ? item.label : ''"
            tooltipPosition="right"
          >
            <i
              [class]="
                'pi pi-' +
                item.icon +
                ' text-primary group-hover:text-inherit text-xl'
              "
            ></i>
            <span tooltipPosition="right" *ngIf="isExpanded" class="ml-2">
              {{ item.label }}
            </span>
            <p-badge *ngIf="item.badge" class="ml-auto" [value]="item.badge" />
            <span
              *ngIf="item.shortcut"
              class="ml-auto border border-surface rounded
                bg-emphasis text-muted-color text-xs p-1"
            >
              {{ item.shortcut }}
            </span>
          </a>
        </ng-template>
      </p-panelmenu>
    </div>
  `
})
export class SidebarComponent implements OnInit {
  @Output() expanded = new EventEmitter<boolean>();
  clickSubMenuOrClick!: boolean;
  isExpanded = false;
  clickPlay!: boolean;

  /** Lista de items. */
  items = input.required<OptionItem[]>();
  /** Indica si los items son seleccionables. */
  selectable = input(false);
  onlyIcons = input(false);

  /** Emite el evento de click sobre un item. */
  @Output() itemClick = new EventEmitter<OptionItem>();

  injector = inject(Injector);
  _items = signal<OptionItem[]>([]);
  menuItems = signal<MenuItem[]>([]);
  selectedItemId = signal<string | null>(null);

  ngOnInit(): void {
    this.isExpanded = false;
    this.clickPlay = false;
    this.clickSubMenuOrClick = false;

    runInInjectionContext(this.injector, () => {
      effect(() => {
        this._items.set(this.items());
        this.menuItems.set(this.convertToMenuItems(this.items()));
        this.isExpanded = this.onlyIcons() ? false : true;
      });
    });
  }

  /**
   * Convierte OptionItem[] a MenuItem[] para PrimeNG
   */
  private convertToMenuItems(items: OptionItem[]): MenuItem[] {
    return items.map((item) => this.mapOptionItemToMenuItem(item));
  }

  private mapOptionItemToMenuItem(item: OptionItem): MenuItem {
    const hasChildren = !!item.children?.length;

    return {
      label: item.title,
      icon: item.icon,
      data: item,
      command: hasChildren ? undefined : () => this.onItemClick(item),
      items: item.children?.map((child) => this.mapOptionItemToMenuItem(child))
    };
  }

  /**
   * Maneja el evento de click sobre un item.
   * @param optionItemSelected item seleccionado.
   */
  onItemClick(optionItemSelected: OptionItem) {
    this.selectedItemId.set(optionItemSelected.id);

    if (this.selectable()) {
      this._items.set(
        this.items().map((item) => {
          return {
            ...item,
            active: item.id === optionItemSelected.id
          };
        })
      );
    }

    this.itemClick.emit(optionItemSelected);
  }

  /**
   * Al darle click en el play activa funcionalidad de expander o contraer
   */
  playSidebar(isExpanded?: boolean) {
    this.clickSubMenuOrClick = true;
    this.isExpanded = isExpanded ?? !this.isExpanded;
    this.clickPlay = this.isExpanded;
    this.expandCollapseAside();
  }

  expandCollapseAside() {
    this.expanded.emit(this.isExpanded);
  }
}
